/**
 * Classification codes (ADR-0016; named "Code lists" until 2026-10-23): document types, areas, classifications and retention
 * rules, shared by every company. Platform admins only. A code is permanent once
 * a document uses it: names, colors and Retired can change, the code can't.
 */
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, type CodeValue } from "../lib/api";
import { useStore } from "../lib/store";
import { applyCodeLists } from "../../../shared/codes";
import { Loading, TypeBadge } from "../components/bits";
import { PartTag } from "./settings/SettingsLayout";

type Kind = CodeValue["kind"];
const KINDS: { kind: Kind; label: string; help: string }[] = [
  { kind: "type", label: "Document types", help: "The type code is part of every document ID, e.g. ITA-POL-SEC-001." },
  { kind: "area", label: "Areas", help: "The functional area (department) is part of the ID. It sorts documents; it never decides who can read them." },
  { kind: "classification", label: "Classifications", help: "Printed on every downloaded page. Restricted limits readers to named people; the others are labels." },
  { kind: "retention", label: "Retention rules", help: "How long a document is kept once retired. A label for now: nothing is deleted automatically." },
];

export function CodeListsPage() {
  const { me, toast } = useStore();
  const [sp, setSp] = useSearchParams();
  const kind = (KINDS.find((k) => k.kind === sp.get("list"))?.kind ?? "type") as Kind;
  const [codes, setCodes] = useState<CodeValue[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const load = async () => {
    try {
      const r = await api.adminCodeLists();
      setCodes(r.codes);
      applyCodeLists(r.codes); // pickers elsewhere in the app see the change at once
    } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);
  if (!me.user.is_platform_admin) return <Navigate to="/" replace />;
  if (err) return <div className="card empty view"><h3>Classification codes didn't load</h3>{err}</div>;
  if (!codes) return <Loading />;

  const meta = KINDS.find((k) => k.kind === kind)!;
  const rows = codes.filter((c) => c.kind === kind && (showRetired || !c.retired)).sort((a, b) => Number(a.retired) - Number(b.retired) || a.sort - b.sort);
  const retiredCount = codes.filter((c) => c.kind === kind && c.retired).length;
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    try { await fn(); toast(msg); await load(); } catch (e) { toast((e as Error).message); }
  };

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <PartTag part="vault" />
          <h1 className="title">Classification <em>codes</em></h1>
          <div className="lede">Document types, areas, classifications and retention rules, shared by every company. Add codes, rename them or retire them. Once a document uses a code it can't change or be deleted, so document IDs never change.</div>
        </div>
      </div>
      <div className="tabs" role="tablist">
        {KINDS.map((k) => (
          <button key={k.kind} className={`tab ${kind === k.kind ? "on" : ""}`} role="tab" aria-selected={kind === k.kind} onClick={() => setSp({ list: k.kind }, { replace: true })}>
            {k.label}<span className="n">{codes.filter((c) => c.kind === k.kind && !c.retired).length}</span>
          </button>
        ))}
      </div>
      <div className="filters">
        <span className="cap">{meta.help}</span>
        <span style={{ flex: 1 }} />
        {retiredCount > 0 && <label className="cap" style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />Show retired ({retiredCount})</label>}
      </div>
      <div className="card tbl-wrap">
        <table className="tbl code-tbl">
          <thead><tr><th style={{ width: 110 }}>Code</th><th>Name</th>{kind === "type" && <th className="hide-md">Short name</th>}<th style={{ width: 110 }}>Status</th><th style={{ width: 210 }}><span className="sr">Actions</span></th></tr></thead>
          <tbody>
            {rows.map((c) => <CodeRow key={c.code} c={c} run={run} />)}
          </tbody>
        </table>
      </div>
      <AddCode kind={kind} run={run} />
      <div className="foot-note">Status codes (DFT, REV, APR, PUB, SUP, ARC, RET) and company codes are reserved, and a code can only be in one list. Every change is in the audit log.</div>
    </div>
  );
}

function CodeRow({ c, run }: { c: CodeValue; run: (fn: () => Promise<unknown>, msg: string) => Promise<void> }) {
  const [edit, setEdit] = useState(false);
  const [label, setLabel] = useState(c.label);
  const [short, setShort] = useState(c.short_label ?? "");
  const [color, setColor] = useState(c.color ?? "#3E4651");
  const save = () => run(() => api.updateCode(c.kind, c.code, { label, ...(c.kind === "type" ? { short_label: short || null, color } : {}) }), `Saved ${c.code}.`).then(() => setEdit(false));
  return (
    <tr className={c.retired ? "retired" : ""}>
      <td><span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>{c.kind === "type" && <TypeBadge type={c.code} sm />}<b className="mono">{c.code}</b></span></td>
      <td>{edit ? <input className="inp inp-sm" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} /> : c.label}
        {c.locked && <span className="cap" title="DocVault behavior depends on this code"> · built in</span>}</td>
      {c.kind === "type" && (
        <td className="hide-md">{edit
          ? <span style={{ display: "flex", gap: 6 }}><input className="inp inp-sm" value={short} maxLength={30} onChange={(e) => setShort(e.target.value)} placeholder="Short name" />
              <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} aria-label="Icon color" style={{ width: 34, border: 0, background: "none" }} /></span>
          : c.short_label ?? ""}</td>
      )}
      <td>{c.retired ? <span className="pill draft">Retired</span> : c.used ? <span className="pill pub">In use</span> : <span className="pill info">Unused</span>}</td>
      <td style={{ textAlign: "right" }}>
        <div className="row-btns" style={{ justifyContent: "flex-end", margin: 0 }}>
          {edit ? <>
            <button className="btn btn-accent btn-sm" onClick={() => void save()}>Save</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(false)}>Cancel</button>
          </> : <>
            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(true)}>Edit</button>
            {!c.locked && <button className="btn btn-ghost btn-sm" onClick={() => void run(() => api.updateCode(c.kind, c.code, { retired: !c.retired }), c.retired ? `${c.code} is available again.` : `${c.code} retired. Existing documents keep it.`)}>{c.retired ? "Restore" : "Retire"}</button>}
            {!c.locked && !c.used && <button className="btn btn-ghost btn-sm" onClick={() => { if (confirm(`Delete ${c.code}? Nothing uses it yet.`)) void run(() => api.removeCode(c.kind, c.code), `${c.code} deleted.`); }}>Delete</button>}
          </>}
        </div>
      </td>
    </tr>
  );
}

function AddCode({ kind, run }: { kind: Kind; run: (fn: () => Promise<unknown>, msg: string) => Promise<void> }) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [short, setShort] = useState("");
  const [color, setColor] = useState("#3E4651");
  const re = kind === "retention" ? /^[A-Z][A-Z0-9]{1,7}$/ : /^[A-Z][A-Z0-9]{1,4}$/;
  const ok = re.test(code) && label.trim().length >= 2;
  const add = () => run(() => api.addCode({ kind, code, label: label.trim(), ...(kind === "type" ? { short_label: short.trim() || undefined, color } : {}) }), `Added ${code}.`)
    .then(() => { setCode(""); setLabel(""); setShort(""); });
  return (
    <div className="card add-code">
      <h3>Add a {kind === "type" ? "document type" : kind === "area" ? "area" : kind === "classification" ? "classification" : "retention rule"}</h3>
      <div className="add-code-row">
        <label><span className="lbl">Code</span><input className="inp mono" value={code} maxLength={kind === "retention" ? 8 : 5} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder={kind === "type" ? "e.g. SOW" : "e.g. DEV"} /></label>
        <label className="grow"><span className="lbl">Name</span><input className="inp" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} placeholder={kind === "type" ? "e.g. Statement of Work" : "Full name"} /></label>
        {kind === "type" && <>
          <label><span className="lbl">Short name</span><input className="inp" value={short} maxLength={30} onChange={(e) => setShort(e.target.value)} placeholder="Optional" /></label>
          <label><span className="lbl">Color</span><input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} style={{ width: 44, height: 38, border: 0, background: "none" }} /></label>
        </>}
        <button className="btn btn-accent" disabled={!ok} onClick={() => void add()}>Add</button>
      </div>
      <div className="help">{kind === "retention" ? "2 to 8" : "2 to 5"} capital letters or digits, starting with a letter. Check it before you add it: once a document uses it, it's permanent.</div>
    </div>
  );
}
